import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom"; // ← Added Link import
import { MDBTable, MDBSpinner, MDBBadge } from "mdb-react-ui-kit";
import axios from "axios";
import "./ViewProductsPage.css";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const DraftProductsPage = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filter & pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [marketplaceFilter, setMarketplaceFilter] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");

  const companyId = localStorage.getItem("company_id");
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDrafts = async () => {
    if (!companyId) {
      setError("Company ID not found");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_HOST}/api/drafts/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        setDrafts(response.data.data);
      } else {
        setError(response.data.error || "Failed to load drafts");
      }
    } catch (err) {
      console.error("Draft fetch error:", err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(
    () => [...new Set(drafts.map((d) => d.category).filter(Boolean))],
    [drafts],
  );
  const sellers = useMemo(
    () => [
      ...new Set(
        drafts
          .map((d) => d.uploadedBy?.name || d.uploaded_by_name)
          .filter(Boolean),
      ),
    ],
    [drafts],
  );

  const filteredDrafts = useMemo(() => {
    return drafts.filter((draft) => {
      const matchesSearch =
        searchQuery === "" ||
        (draft.title?.toLowerCase() || "").includes(
          searchQuery.toLowerCase(),
        ) ||
        (draft.description?.toLowerCase() || "").includes(
          searchQuery.toLowerCase(),
        );

      const matchesCategory =
        categoryFilter === "" || draft.category === categoryFilter;

      const matchesMarketplace = marketplaceFilter === "";

      const sellerName = draft.uploadedBy?.name || draft.uploaded_by_name || "";
      const matchesSeller = sellerFilter === "" || sellerName === sellerFilter;

      return (
        matchesSearch && matchesCategory && matchesMarketplace && matchesSeller
      );
    });
  }, [drafts, searchQuery, categoryFilter, marketplaceFilter, sellerFilter]);

  const totalPages = Math.ceil(filteredDrafts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDrafts = filteredDrafts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setCategoryFilter("");
    setMarketplaceFilter("");
    setSellerFilter("");
    setCurrentPage(1);
  };

  const handleEditContinue = (draft) => {
    navigate("/products/add", { state: { cloneProduct: draft } });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Draft Products</h2>
        <button
          className="btn btn-red"
          onClick={() => navigate("/products/add")}
        >
          + New Product
        </button>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-item">
            <label>Search</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search drafts..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="filter-item">
            <label>Category</label>
            <select
              className="filter-select"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Marketplace</label>
            <select
              className="filter-select"
              value={marketplaceFilter}
              onChange={(e) => {
                setMarketplaceFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Marketplaces</option>
              <option value="ebay">eBay</option>
              <option value="shopify">Shopify</option>
              <option value="poshmark">Poshmark</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Seller</label>
            <select
              className="filter-select"
              value={sellerFilter}
              onChange={(e) => {
                setSellerFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Sellers</option>
              {sellers.map((seller) => (
                <option key={seller} value={seller}>
                  {seller}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Items per page</label>
            <select
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>

          <div className="filter-item">
            <button
              className="btn btn-secondary clear-filters-btn"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {(searchQuery ||
          categoryFilter ||
          marketplaceFilter ||
          sellerFilter) && (
          <div className="active-filters">
            <small className="text-muted">
              Showing {filteredDrafts.length} of {drafts.length} drafts
            </small>
            {searchQuery && (
              <span className="filter-badge">Search: "{searchQuery}"</span>
            )}
            {categoryFilter && (
              <span className="filter-badge">Category: {categoryFilter}</span>
            )}
            {marketplaceFilter && (
              <span className="filter-badge">
                Marketplace: {marketplaceFilter}
              </span>
            )}
            {sellerFilter && (
              <span className="filter-badge">Seller: {sellerFilter}</span>
            )}
          </div>
        )}
      </div>

      {paginatedDrafts.length === 0 ? (
        <div className="empty-state text-center py-5">
          <i className="fas fa-box-open"></i>
          <p>No draft products match your filters.</p>
        </div>
      ) : (
        <>
          <MDBTable hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>SKU</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Images</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDrafts.map((draft) => (
                <tr key={draft.id}>
                  <td>{draft.id}</td>
                  <td>{draft.title || "Untitled"}</td>
                  <td>
                    <MDBBadge color="secondary" pill>
                      {draft.sku}
                    </MDBBadge>
                  </td>
                  <td>${draft.price}</td>
                  <td>{draft.quantity}</td>
                  <td>
                    {draft.images && draft.images.length > 0 ? (
                      <img
                        src={draft.images[0]}
                        alt="thumbnail"
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      "No image"
                    )}
                  </td>
                  <td>{new Date(draft.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => handleEditContinue(draft)}
                    >
                      Edit & Continue
                    </button>
                    <Link
                      to={`/products/${draft.id}`}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </MDBTable>

          {totalPages > 1 && (
            <>
              <div className="pagination">
                <div
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                </div>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <div
                      key={pageNum}
                      className={`page-item ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </div>
                  );
                })}

                <div
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
              <div className="pagination-info">
                Page {currentPage} of {totalPages} ({filteredDrafts.length}{" "}
                items)
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DraftProductsPage;
